import { Box, ButtonBase, Divider, List, Tooltip } from '@mui/material'
import PushPinIcon from '@mui/icons-material/PushPin'
import { blue } from '@mui/material/colors'
import { useLocation } from 'react-router-dom'
import { PAGE_URLS } from '../../utils/helpers/helper'
import { MENU_ITEMS } from '../../utils/initialisations/router'
import { useAuthContext } from '../../context/auth/AuthContextProvider'
import BasicListItem from '../../components/layout/leftMenu/BasicListItem'
import ListItemWithSubMenu from '../../components/layout/leftMenu/ListItemWithSubMenu'
import { TLeftMenu, TMenuItems } from '../../utils/types/layoutTypes'
import CompanyLogo from '../../components/layout/leftMenu/companyLogo'
import UserAvatar from '../../components/layout/leftMenu/UserAvatar'
import NotificationMenuList from '../../components/layout/leftMenu/NotificationMenuList'
import SupportMenuList from '../../components/layout/leftMenu/SupportMenuList'
import JobsMenuListWithStatus from '../../components/layout/leftMenu/JobsMenuListWithStatus'
import dayjs from 'dayjs'
import { useLocalStorage } from '../../hooks/useLocalStorage'

const LeftMenu = ({
  navMenuOpen,
  setNavMenuOpen,
  storeNavMenuOpen,
  setStoreNavMenuOpen,
}: TLeftMenu) => {
  const { user } = useAuthContext()
  const location = useLocation()
  const pathName = `/${location.pathname.split('/')[1]}` ?? PAGE_URLS.DASHBOARD
  const { getItem: getAcknowledgedVersion } = useLocalStorage(
    'cl_acknowledged_version'
  )
  const acknowledgedVersion = getAcknowledgedVersion() as
    | { id: string; version: string }
    | undefined

  return (
    <Box
      className="nav-menu"
      sx={{
        backgroundColor: 'var(--bay-of-many)',
        color: 'var(--white)',
        boxShadow: '5px 0 5px -5px grey',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        overflow: 'hidden',
        transition: 'all var(--global-transition-timing)',
      }}
    >
      <List sx={{ pt: 0 }}>
        <Box
          px={'15px'}
          pt={1}
          sx={{ width: '100%' }}
          className={`company-logo ${navMenuOpen ? 'is-opened' : ''}`}
        >
          <Box width={'120px'}>
            <CompanyLogo />
          </Box>
        </Box>
        <Divider
          variant="middle"
          sx={{
            bgcolor: 'var(--white)',
            mt: 2,
            mb: 1,
            opacity: 0.5,
          }}
        />
        {!user.companyOptions ||
          (!user.companyOptions.includes('MENU_JOBS_STATUS') && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip
                title={`${navMenuOpen ? '' : 'Open menu'}`}
                placement="right"
              >
                <ButtonBase
                  className={`menu-icon ${navMenuOpen ? 'is-opened' : ''}`}
                  sx={{
                    cursor: 'pointer',
                    p: 2,
                  }}
                  onClick={() => {
                    setNavMenuOpen((prev) => !prev)
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 100 100">
                    <path
                      className="line line1"
                      d="M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058"
                    />
                    <path className="line line2" d="M 20,50 H 80" />
                    <path
                      className="line line3"
                      d="M 20,70.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942"
                    />
                  </svg>
                </ButtonBase>
              </Tooltip>
              <ButtonBase
                sx={{
                  color: storeNavMenuOpen ? blue[300] : '#fff',
                  cursor: 'pointer',
                  transform: 'rotate(90deg)',
                  transition: 'color var(--global-transition-timing)',
                }}
                onClick={() => {
                  setStoreNavMenuOpen((prev) => !prev)
                }}
              >
                <PushPinIcon
                  sx={{
                    p: 2,
                    opacity: navMenuOpen ? 1 : 0,
                  }}
                />
              </ButtonBase>
            </Box>
          ))}

        {MENU_ITEMS.map((item: TMenuItems, idx) => {
          if (
            !user.companyOptions ||
            (item.requiredCompanyOption.length > 0 &&
              user.companyOptions.find((option: string) =>
                item.requiredCompanyOption.includes(option)
              ) === undefined) ||
            (item.requiredRoles &&
              user.roles.find((role: string) =>
                item.requiredRoles?.includes(role)
              ) === undefined)
          ) {
            return ''
          }

          if (item.subMenus) {
            return (
              <ListItemWithSubMenu
                key={`liwsm-${idx}`}
                index={idx}
                fullPath={location.pathname}
                listItem={item}
                navMenuOpen={navMenuOpen}
                setNavMenuOpen={setNavMenuOpen}
              />
            )
          }

          if (
            !user.organisationMapsApiKey &&
            ['Map View', 'Routes Plan'].includes(item.label)
          ) {
            return ''
          }

          if (item.label === 'Notifications') {
            return (
              <NotificationMenuList
                key={`blt-${idx}`}
                index={idx}
                selected={pathName === item.goToUrl}
                listItem={item}
                navMenuOpen={navMenuOpen}
              />
            )
          }

          if (item.label === 'Support') {
            return (
              <SupportMenuList
                key={`blt-${idx}`}
                index={idx}
                selected={pathName === item.goToUrl}
                listItem={item}
                navMenuOpen={navMenuOpen}
              />
            )
          }

          if (
            item.label === 'Jobs' &&
            user.companyOptions.includes('MENU_JOBS_STATUS')
          ) {
            return (
              <JobsMenuListWithStatus
                key={`liwsm-${idx}`}
                index={idx}
                fullPath={location.pathname}
                listItem={item}
              />
            )
          }

          return (
            <BasicListItem
              key={`blt-${idx}`}
              index={idx}
              selected={pathName === item.goToUrl}
              listItem={item}
              navMenuOpen={navMenuOpen}
            />
          )
        })}
        <Divider
          variant="middle"
          sx={{
            bgcolor: 'var(--white)',
            mt: 2,
            mb: 1,
            opacity: 0.5,
          }}
        />
        <UserAvatar navMenuOpen={navMenuOpen} />
      </List>

      {navMenuOpen && (
        <Box
          pl={'15px'}
          sx={{
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
            height: '50px',
            fontSize: '0.8rem',
            fontWeight: 700,
          }}
        >
          {acknowledgedVersion && acknowledgedVersion.version
            ? `v${acknowledgedVersion.version}`
            : `v${dayjs().format('YYYY')}`}
          <span style={{ fontWeight: 500, marginLeft: '3px', opacity: 0.7 }}>
            {process.env.REACT_APP_BUILD_ENV}
          </span>
        </Box>
      )}
    </Box>
  )
}

export default LeftMenu
